import type { ReactElement, ReactNode } from 'react';
import styled from '@emotion/styled';
import { Constants } from '@trinity/chat-components/components/Theme';
import { shouldForwardProp } from '@trinity/chat-components/utils/emotion';

type ChatHeaderPresentationProps = {
  className?: string;
  isLoading: boolean;
  shrinkToContent: boolean;
  navigation?: ReactNode;
  filterAction?: ReactNode;
  contextSelector?: ReactNode;
  contextDisplay?: ReactNode;
  inlineEndAction?: ReactNode;
  tabs?: ReactNode;
  endAction?: ReactNode;
};

function ChatHeaderPresentation({
  className,
  isLoading,
  shrinkToContent,
  navigation,
  filterAction,
  contextSelector,
  contextDisplay,
  inlineEndAction,
  tabs,
  endAction,
}: ChatHeaderPresentationProps): ReactElement {
  return (
    <Root className={className} shrinkToContent={shrinkToContent}>
      <Wrap>
        {!isLoading && (
          <>
            <TabBar>
              <Left>
                {navigation}
                {filterAction}
                {contextSelector}
                {contextDisplay}
                {inlineEndAction}
              </Left>
              {tabs}
            </TabBar>
            {endAction && <Right>{endAction}</Right>}
          </>
        )}
      </Wrap>
    </Root>
  );
}

export { ChatHeaderPresentation };
export type { ChatHeaderPresentationProps };

const Root = styled('div', { shouldForwardProp: shouldForwardProp('shrinkToContent') })<{
  shrinkToContent?: boolean;
}>(({ theme, shrinkToContent }) => ({
  position: 'sticky',
  top: '0',
  height: `${Constants.Modal.HeaderHeight}px`,
  boxSizing: 'border-box',
  padding: '10px',
  ...(shrinkToContent
    ? {
        width: 'fit-content',
        border: 'none',
        background: theme.palette.white.main,
      }
    : {
        border: `1px solid ${theme.palette.gray.light2}`,
        background: theme.palette.white.main,
      }),
}));

const Wrap = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '100%',
});

const TabBar = styled.div({
  display: 'flex',
  gap: '15px',
  minWidth: 0,
  flex: 1,
});

const Left = styled.div({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  minWidth: 0,
  flex: 1,
});

const Right = styled.div({
  cursor: 'pointer',
  marginRight: '8px',
});
